import EmailIcon from '@mui/icons-material/Email'
import KeyIcon from '@mui/icons-material/Key'
import PersonIcon from '@mui/icons-material/Person'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Alert, Box, Button, Card, CardContent, Divider, InputAdornment, Link, TextField, Typography } from '@mui/material'
import * as React from 'react'
import { useLocation, useNavigate } from 'react-router'

import AuthService from '../../services/AuthService'
import SocialLoginButtons from '../common/SocialLoginButtons'

export default function Signup() {
  const location = useLocation()
  const navigate = useNavigate()
  const [formData, setFormData] = React.useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    sports: ['SKI'], // Default sport
  })
  const [error, setError] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [passwordError, setPasswordError] = React.useState('')
  const [confirmPasswordError, setConfirmPasswordError] = React.useState('')
  const [emailError, setEmailError] = React.useState('')
  const [showPassword, setShowPassword] = React.useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false)

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long'
    }
    return ''
  }

  const validateConfirmPassword = (password, confirmPassword) => {
    if (password !== confirmPassword) {
      return 'Passwords do not match'
    }
    return ''
  }

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!email) {
      return 'Email is required'
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address'
    }
    return ''
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })

    // Clear error when user starts typing
    setError('')

    // Validate fields on change
    if (name === 'password') {
      const error = validatePassword(value)
      setPasswordError(error)

      if (formData.confirmPassword) {
        const confirmError = validateConfirmPassword(value, formData.confirmPassword)
        setConfirmPasswordError(confirmError)
      }
    } else if (name === 'confirmPassword') {
      const error = validateConfirmPassword(formData.password, value)
      setConfirmPasswordError(error)
    } else if (name === 'email') {
      const error = validateEmail(value)
      setEmailError(error)
    }
  }

  const handleSubmit = React.useCallback(
    async (e) => {
      e.preventDefault()
      setError('')

      // Validate all fields before submitting
      const pwError = validatePassword(formData.password)
      const confirmPwError = validateConfirmPassword(formData.password, formData.confirmPassword)
      const emError = validateEmail(formData.email)

      // Update error states
      setPasswordError(pwError)
      setConfirmPasswordError(confirmPwError)
      setEmailError(emError)

      // Check for any validation errors
      if (pwError || confirmPwError || emError || !formData.firstName) {
        return setError('Please correct the errors in the form')
      }

      setIsSubmitting(true)

      try {
        await AuthService.signup(formData)
        navigate('/login', {
          state: {
            message: 'Account created successfully! Please sign in.',
            redirectTo: location?.state?.redirectTo,
          },
        })
      } catch (error) {
        // Handle different error cases based on the API response
        if (error.response) {
          switch (error.response.status) {
            case 409:
              setError('An account with this email already exists')
              break
            case 400:
              const message = error.response.data?.message
              if (message?.includes('password')) {
                setError('Password must be at least 8 characters long')
              } else if (message?.includes('email')) {
                setError('Please enter a valid email address')
              } else {
                setError(message || 'Please check your input and try again')
              }
              break
            case 429:
              setError('Too many attempts. Please try again later')
              break
            default:
              setError('Failed to create account. Please try again')
          }
        } else if (error.request) {
          setError('Network error. Please check your connection')
        } else {
          setError('Something went wrong. Please try again')
        }
      } finally {
        setIsSubmitting(false)
      }
    },
    [formData, navigate],
  )

  const handleSignIn = (e) => {
    e.preventDefault()
    navigate('/login', {
      state: {
        redirectTo: location?.state?.redirectTo,
      },
    })
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        alignItems: 'center',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: '400px', mt: 4 }}>
        <CardContent>
          <Typography variant="h5" component="h1" gutterBottom>
            Create Account
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <SocialLoginButtons mode="signup" onSetError={setError} />

          <Divider sx={{ my: 2 }}>or sign up with email</Divider>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              error={!!emailError}
              helperText={emailError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              error={!!passwordError}
              helperText={passwordError || 'Must be at least 8 characters long'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {showPassword ? (
                      <VisibilityOffIcon
                        fontSize="small"
                        sx={{
                          cursor: isSubmitting ? 'default' : 'pointer',
                          color: isSubmitting ? 'text.disabled' : 'action.active',
                          '&:hover': {
                            color: isSubmitting ? 'text.disabled' : 'primary.main',
                          },
                        }}
                        onClick={() => !isSubmitting && setShowPassword(false)}
                      />
                    ) : (
                      <VisibilityIcon
                        fontSize="small"
                        sx={{
                          cursor: isSubmitting ? 'default' : 'pointer',
                          color: isSubmitting ? 'text.disabled' : 'action.active',
                          '&:hover': {
                            color: isSubmitting ? 'text.disabled' : 'primary.main',
                          },
                        }}
                        onClick={() => !isSubmitting && setShowPassword(true)}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              error={!!confirmPasswordError}
              helperText={confirmPasswordError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {showPassword ? (
                      <VisibilityOffIcon
                        fontSize="small"
                        sx={{
                          cursor: isSubmitting ? 'default' : 'pointer',
                          color: isSubmitting ? 'text.disabled' : 'action.active',
                          '&:hover': {
                            color: isSubmitting ? 'text.disabled' : 'primary.main',
                          },
                        }}
                        onClick={() => !isSubmitting && setShowPassword(false)}
                      />
                    ) : (
                      <VisibilityIcon
                        fontSize="small"
                        sx={{
                          cursor: isSubmitting ? 'default' : 'pointer',
                          color: isSubmitting ? 'text.disabled' : 'action.active',
                          '&:hover': {
                            color: isSubmitting ? 'text.disabled' : 'primary.main',
                          },
                        }}
                        onClick={() => !isSubmitting && setShowPassword(true)}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              margin="normal"
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              disabled={isSubmitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Button type="submit" fullWidth variant="contained" sx={{ mt: 2, mb: 1 }} disabled={isSubmitting || !!passwordError || !!emailError || !!confirmPasswordError}>
              {isSubmitting ? 'Creating Account...' : 'Sign Up'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleSignIn}
                  sx={{
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Sign in
                </Link>
              </Typography>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  )
}
